import {createSelector} from 'reselect'
import {
  getState,
  formSelector,
  formsSelector,
  updateForm,
} from '../../../store.js'
import {locationSelector} from '../../../redux/selectors/ui/location.js'
import {
  NEW_ID,
  SETTINGS,
  SHIPPERS,
} from '../../../common/constants/SettingsPanels.js'
import {isPositiveNumeric, isPresent} from '../../../common/utils.js'
import {activeShippersSelector, setShipper} from '../../../data/shippers.js'
import {
  canUseFedEx360Selector,
  canUsePitneyMerchantSelector,
  canUsePitneySelector,
  canUseUSPSShipSelector,
  shippersEnabledSelector,
} from '../../../data/company.js'
import {
  FEDEX_360,
  PITNEY,
  PITNEY_MERCHANT,
  SENDLE,
  SHIPPER_OPTIONS,
  SHIPPO,
  USPS_SHIP,
} from '../../../common/constants/ShipperNames.js'
import {navigate} from '../../../common/location.js'
import api from '../../../common/api.js'
import {showGlobalError} from '../../GlobalErrorMessage.js'

export const SETTINGS__SHIPPERS = 'settings__shippers'

export function setupShippersForm() {
  return {
    formName: SETTINGS__SHIPPERS,
    initialForm: {
      newVendor: '',
      isSaving: false,
      serverError: null,
    },
  }
}

export function updateShippersForm(...args) {
  updateForm(SETTINGS__SHIPPERS, ...args)
}

export function shippersFormSelector(state) {
  return (
    formsSelector(state)[SETTINGS__SHIPPERS] || shippersFormSelector.default
  )
}
shippersFormSelector.default = {}

export const settingsShippersShipperIDSelector = createSelector(
  locationSelector,
  ({pathComponents}) => {
    if (
      pathComponents[0] === SETTINGS &&
      pathComponents[1] === SHIPPERS &&
      isPositiveNumeric(pathComponents[2])
    ) {
      return Number(pathComponents[2])
    }

    return null
  },
)

export const settingsShippersNewSelector = createSelector(
  locationSelector,
  ({pathComponents}) => {
    return (
      pathComponents[0] === SETTINGS &&
      pathComponents[1] === SHIPPERS &&
      pathComponents[2] === NEW_ID
    )
  },
)

export const newShipperOptionsSelector = createSelector(
  activeShippersSelector,
  canUsePitneySelector,
  canUsePitneyMerchantSelector,
  canUseUSPSShipSelector,
  canUseFedEx360Selector,
  shippersEnabledSelector,
  (
    shippers,
    canUsePitney,
    canUsePitneyMerchant,
    canUseUSPSShip,
    canUseFedEx360,
    shippersEnabled,
  ) => {
    let options = SHIPPER_OPTIONS

    // Sendle is a zombie shipper for now, so no new sign ups
    options = options.filter((option) => option.value !== SENDLE)

    if (shippersEnabled) {
      options = SHIPPER_OPTIONS.filter((option) =>
        shippersEnabled.includes(option.value),
      )
    } else {
      // then strip out shippers that are only controlled by shippers_enabled
      options = options.filter((option) => ![SHIPPO].includes(option.value))
    }

    const hasPitney = !!shippers.find((shipper) => shipper.vendor === PITNEY)

    if (hasPitney || !canUsePitney || canUsePitneyMerchant) {
      options = options.filter((option) => option.value !== PITNEY)
    }

    if (!canUsePitneyMerchant) {
      options = options.filter((option) => option.value !== PITNEY_MERCHANT)
    }

    if (!canUseUSPSShip) {
      options = options.filter((option) => option.value !== USPS_SHIP)
    }

    if (!canUseFedEx360) {
      options = options.filter((option) => option.value !== FEDEX_360)
    }

    return options
  },
)

export function baseShipperFormErrorsSelector(state, {formName}) {
  const errors = {}
  const form = formSelector(state, {
    formName,
  })

  if (form.id !== NEW_ID && !isPresent(form.name)) {
    errors.name = 'Nickname is required'
    errors.preventSave = true
  }

  return errors
}

export function baseShipperInitialFormSelector(state, {shipper, vendor}) {
  shipper ||= {id: NEW_ID, vendor}

  return {
    id: shipper.id,
    vendor: shipper.vendor,
    name: shipper.name || '',
    hidden_shipping_methods: shipper.hidden_shipping_methods || [],
    isSaving: false,
  }
}

export function baseSaveParamsSelector(state, {formName}) {
  const form = formSelector(state, {formName})

  return [
    form.id,
    {
      name: form.id === NEW_ID ? undefined : form.name,
      hidden_shipping_methods:
        form.id === NEW_ID ? undefined : form.hidden_shipping_methods,
    },
  ]
}

export async function simpleSaveShipper(formName, extraParams) {
  const [shipperID, params] = baseSaveParamsSelector(getState(), {formName})

  if (shipperID === NEW_ID) {
    return
  }

  updateForm(formName, {isSaving: true})

  try {
    const {json} = await api.put(`/shipper/${shipperID}/`, {
      ...params,
      ...extraParams,
    })

    goToShipperSettings()

    setShipper(json)
  } catch (err) {
    showGlobalError(
      {
        details: err.error_message || err.message,
        summary: 'There was an error while saving shipper.',
      },
      err,
    )
  } finally {
    updateForm(formName, {isSaving: false})
  }
}

export function goToShipperSettings() {
  return navigate([SETTINGS, SHIPPERS])
}
